import { Canvas, useFrame } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Group } from "three";
import gsap from "gsap";
import type { StoreIndex } from "../data/store";
import {
    CASINGS,
    CPP_STYLES,
    generate,
    getLanguage,
    type Casing,
    type CppStyle,
} from "../lib/format";
import { useStruct } from "../hooks/useStruct";
import { CodeViewer } from "./CodeViewer";
import { LangLogo } from "./LangLogo";

type GlyphShape = "icosahedron" | "octahedron" | "torus" | "boxes";

const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ----------------------------- Three.js glyph ---------------------------- */

function GlyphMesh({ shape }: { shape: GlyphShape }) {
    if (shape === "boxes") {
        const cells = [-0.55, 0.55];
        return (
            <group>
                {cells.flatMap((x) =>
                    cells.flatMap((y) =>
                        cells.map((z) => (
                            <mesh key={`${x}${y}${z}`} position={[x, y, z]}>
                                <boxGeometry args={[0.7, 0.7, 0.7]} />
                                <meshBasicMaterial color="#ff2d95" wireframe />
                            </mesh>
                        )),
                    ),
                )}
            </group>
        );
    }
    return (
        <mesh>
            {shape === "icosahedron" && <icosahedronGeometry args={[1.25, 0]} />}
            {shape === "octahedron" && <octahedronGeometry args={[1.35, 0]} />}
            {shape === "torus" && <torusGeometry args={[0.85, 0.32, 16, 60]} />}
            <meshBasicMaterial color="#ff2d95" wireframe />
        </mesh>
    );
}

function Spin({ enabled, children }: { enabled: boolean; children: ReactNode }) {
    const ref = useRef<Group>(null);
    useFrame((_, delta) => {
        if (enabled && ref.current) {
            ref.current.rotation.y += delta * 0.45;
            ref.current.rotation.x += delta * 0.12;
        }
    });
    return <group ref={ref}>{children}</group>;
}

function Glyph({ shape }: { shape: GlyphShape }) {
    const motion = !prefersReducedMotion();
    return (
        <Canvas className="glyph-canvas" camera={{ position: [0, 0, 4], fov: 45 }} gl={{ alpha: true, antialias: true }}>
            <Float speed={motion ? 2 : 0} rotationIntensity={motion ? 1 : 0} floatIntensity={motion ? 0.8 : 0}>
                <Spin enabled={motion}>
                    <GlyphMesh shape={shape} />
                </Spin>
            </Float>
        </Canvas>
    );
}

/* -------------------------------- view ----------------------------------- */

type View = {
    content: string;
    language: string;
    filename: string;
    glyph: GlyphShape;
    updated?: string;
    casing?: Casing;
    cppStyle?: CppStyle;
    showCasing: boolean;
    showStyle: boolean;
};

function formatBytes(n: number): string {
    return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`;
}

/* ------------------------------ modal inner ------------------------------ */

function ModalContent({
    view,
    onOption,
}: {
    view: View;
    onOption: (patch: { casing?: Casing; cppStyle?: CppStyle }) => void;
}) {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(view.content);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    };

    const download = () => {
        const blob = new Blob([view.content], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = view.filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const lines = view.content.replace(/\n$/, "").split("\n").length;

    return (
        <>
            <header className="modal-head">
                <div className="modal-glyph">
                    <Glyph shape={view.glyph} />
                </div>

                <div className="modal-id">
                    <div className="modal-file">
                        <LangLogo language={view.language} />
                        <span className="modal-filename">{view.filename}</span>
                    </div>
                    <div className="modal-meta">
                        {view.updated && (
                            <>
                                <span>updated {view.updated}</span>
                                <span>·</span>
                            </>
                        )}
                        <span>{lines} lines</span>
                        <span>·</span>
                        <span>{formatBytes(view.content.length)}</span>
                    </div>
                </div>

                <div className="modal-actions">
                    <button type="button" className="modal-btn" onClick={copy}>
                        {copied ? "Copied" : "Copy"}
                    </button>
                    <button type="button" className="modal-btn" onClick={download}>
                        Download
                    </button>
                </div>
            </header>

            {(view.showCasing || view.showStyle) && (
                <div className="modal-toolbar">
                    {view.showCasing && (
                        <label className="modal-field">
                            <span>Casing</span>
                            <select
                                className="modal-select"
                                value={view.casing}
                                onChange={(e) => onOption({ casing: e.target.value as Casing })}
                            >
                                {CASINGS.map((c) => (
                                    <option key={c.key} value={c.key}>{c.label}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    {view.showStyle && (
                        <label className="modal-field">
                            <span>Style</span>
                            <select
                                className="modal-select"
                                value={view.cppStyle}
                                onChange={(e) => onOption({ cppStyle: e.target.value as CppStyle })}
                            >
                                {CPP_STYLES.map((s) => (
                                    <option key={s.key} value={s.key}>{s.label}</option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>
            )}

            <div className="modal-body">
                <CodeViewer code={view.content} />
            </div>
        </>
    );
}

/* -------------------------------- modal ---------------------------------- */

export function PatternModal({
    category,
    dumpKey,
    casing,
    cppStyle,
    store,
    storeError,
    onClose,
    onOption,
}: {
    category: string | null;
    dumpKey: string | null;
    casing: string | null;
    cppStyle: string | null;
    store: StoreIndex | null;
    storeError: string | null;
    onClose: () => void;
    onOption: (patch: { casing?: Casing; cppStyle?: CppStyle }) => void;
}) {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const open = Boolean(category && dumpKey);
    const isStruct = category === "structs";
    const struct = useStruct(isStruct ? dumpKey : null);

    useEffect(() => {
        const dlg = dialogRef.current;
        if (!dlg) return;
        if (open && !dlg.open) {
            dlg.showModal();
            document.body.style.overflow = "hidden";
            if (!prefersReducedMotion() && panelRef.current) {
                gsap.from(panelRef.current, { opacity: 0, y: 22, scale: 0.96, duration: 0.42, ease: "power3.out" });
            }
        } else if (!open && dlg.open) {
            dlg.close();
        }
        if (!open) document.body.style.overflow = "";
    }, [open]);

    useEffect(() => () => void (document.body.style.overflow = ""), []);

    const view = useMemo<View | null>(() => {
        if (!category || !dumpKey) return null;

        if (category === "structs") {
            if (!struct.data) return null;
            return {
                content: struct.data.content,
                language: "cpp",
                filename: `${struct.data.name}.hpp`,
                glyph: "boxes",
                updated: struct.data.updated,
                showCasing: false,
                showStyle: false,
            };
        }

        if (!store) return null;
        const updated = store.meta.lastUpdated;

        if (category === "declarations") {
            return {
                content: generate("declarations", store.patterns, { casing: "pascal", cppStyle: "define" }, updated),
                language: "cpp",
                filename: "declarations.h",
                glyph: "torus",
                updated,
                showCasing: false,
                showStyle: false,
            };
        }

        // patterns
        const lang = getLanguage(dumpKey);
        if (!lang) return null;
        const effCasing = (casing as Casing | null) ?? lang.defaultCasing;
        const effStyle = (cppStyle as CppStyle | null) ?? "define";
        return {
            content: generate(lang.key, store.patterns, { casing: effCasing, cppStyle: effStyle }, updated),
            language: lang.language,
            filename: lang.filename,
            glyph: "icosahedron",
            updated,
            casing: effCasing,
            cppStyle: effStyle,
            showCasing: true,
            showStyle: Boolean(lang.hasCppStyle),
        };
    }, [category, dumpKey, casing, cppStyle, store, struct.data]);

    const loading = open && !view && !storeError && !struct.error;
    const error = isStruct ? struct.error : storeError;

    const handleBackdrop = (e: React.MouseEvent<HTMLDialogElement>) => {
        if (e.target === dialogRef.current) onClose();
    };

    return (
        <dialog ref={dialogRef} className="modal" onClick={handleBackdrop} onClose={onClose} onCancel={onClose}>
            {open && (
                <div ref={panelRef} className="modal-panel">
                    <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>
                        ✕
                    </button>

                    {loading && (
                        <div className="modal-state">
                            <div className="scanline" />
                            <span>generating…</span>
                        </div>
                    )}

                    {error && (
                        <div className="modal-state modal-error">
                            <span>could not load — {error}</span>
                        </div>
                    )}

                    {view && <ModalContent view={view} onOption={onOption} />}
                </div>
            )}
        </dialog>
    );
}
